/**
 * Mutation operations for RFP and coverage data
 *
 * Low-level API client functions for data modifications (create, update, delete).
 * These functions handle the actual HTTP requests or mock implementations.
 * They are consumed by React Query hooks in mutations.ts.
 */
import { logger } from "@platform/browser-logger";
import { type BenefitTransformToApiOptions } from "@platform/btx-benefit-transforms";
import { AxiosInstance } from "axios";

import { RfpBenefitPlan } from "../types/rfp.api.types";

import { liftRateGuaranteeFromBenefits } from "./rateGuaranteeFields";
import { transformBenefitsForSave } from "./transformBenefitsForSave";
import {
  BatchUpdateCoverageRequest,
  BatchUpdateCoverageResponse,
  RfpCoverage,
  RfpNotePayload,
  AnalystNotePayload,
  SaveRfpRequest,
  SaveRfpResponse,
} from "./types";

import type { LocCoverageType } from "../locs/locMap";
import type { WholeLifeBenefitsApi } from "../locs/whole-life/types";

/**
 * Creates a benefit plan update payload
 */
const createBenefitPlanPayload = (
  benefitPlan: RfpBenefitPlan,
  coverageType: LocCoverageType,
  benefitTransformOptions?: BenefitTransformToApiOptions,
): Partial<RfpBenefitPlan> => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const transformedBenefits = transformBenefitsForSave(
    benefitPlan.benefits,
    coverageType,
    benefitTransformOptions,
  );

  // Guard eligibilityClasses: must be an array of {id, name} objects or undefined.
  // AG Grid's auto cell data type detection can coerce arrays to strings (e.g.,
  // "[object Object]") if the initial cell value was a string. This guard
  // prevents sending a string to the backend which expects []EligibilityClassResponse.
  const eligibilityClasses = Array.isArray(benefitPlan.eligibilityClasses)
    ? benefitPlan.eligibilityClasses
    : undefined;

  return {
    type: benefitPlan.type,
    fundingType: benefitPlan.fundingType,
    participationRequirements: benefitPlan.participationRequirements,
    optionName: benefitPlan.optionName,
    benefitClassAvailability: benefitPlan.benefitClassAvailability,
    // Note: rateSchedule is intentionally omitted. Rates are managed exclusively
    // through the dedicated rate-schedules/rates APIs (RatesGrid & RatesDrawer).
    currentCarrierName: benefitPlan.currentCarrierName,
    // Edited as benefit-body cells, persisted at plan level.
    ...liftRateGuaranteeFromBenefits(benefitPlan.benefits),
    eligibilityClasses,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    benefits: transformedBenefits,
  };
};

/**
 * Updates a single benefit plan via PATCH request.
 * Backend sanitizes invalid fields in place and returns their JSON field paths.
 */
const updateBenefitPlan = async (
  axiosInstance: AxiosInstance,
  planId: string,
  payload: Partial<RfpBenefitPlan>,
): Promise<string[]> => {
  try {
    const response = await axiosInstance.patch<{ invalidFields: string[] }>(
      `/btx/v2/benefit-plans/${planId}`,
      payload,
    );
    return response.data.invalidFields ?? [];
  } catch (error) {
    logger.error(
      `[BTX API] Failed to update benefit plan ${planId}`,
      error as Error,
      {
        errorResponse: (error as { response: { data: string } }).response?.data,
      },
    );
    throw error;
  }
};

const PERSON_LABELS: Record<string, string> = {
  employee: "Employee",
  spouse: "Spouse",
  child: "Child",
};

const WHOLE_LIFE_DEPENDENT_FIELDS: {
  field: keyof WholeLifeBenefitsApi;
  label: string;
}[] = [
  { field: "spouseBenefitAmount", label: "Benefit Amount - Spouse" },
  { field: "spouseBenefitMaximum", label: "Benefit Maximum - Spouse" },
  { field: "childBenefitAmount", label: "Benefit Amount - Child" },
  { field: "childBenefitMaximum", label: "Benefit Maximum - Child" },
];

/**
 * Returns a list of incomplete benefit field descriptions for a plan payload.
 * Empty array means the payload is valid.
 */
export const getIncompleteBenefitFields = (
  payload: Partial<RfpBenefitPlan>,
): string[] => {
  const benefits = payload.benefits as
    | Record<
        string,
        { benefitDescription?: { type?: string; amounts?: number[] } }
      >
    | undefined;
  if (!benefits || typeof benefits !== "object") return [];

  const incompleteFields: string[] = [];

  (["employee", "spouse", "child"] as const).forEach((person) => {
    const desc = benefits[person]?.benefitDescription;
    if (
      desc?.type === "multipleOptions" &&
      (!Array.isArray(desc.amounts) || desc.amounts.length === 0)
    ) {
      incompleteFields.push(
        `Benefit Description - ${PERSON_LABELS[person]} Amount`,
      );
    }
  });

  const wlBenefits = benefits as unknown as WholeLifeBenefitsApi;
  if (wlBenefits.objectType === "wholeLife") {
    for (const { field, label } of WHOLE_LIFE_DEPENDENT_FIELDS) {
      const benefit = wlBenefits[field] as
        | { type?: string; amount?: number }
        | undefined;
      if (
        benefit?.type === "dollarAmount" &&
        (benefit.amount == null || benefit.amount <= 0)
      ) {
        incompleteFields.push(label);
      }
    }
  }

  return incompleteFields;
};

type PlanSavePromise = Promise<{ planId: string; invalidFields: string[] }>;

/**
 * Processes benefit plans in a coverage and returns update promises.
 * Only PATCHes plans that have been explicitly marked as dirty via
 * _dirtyPlanIds. If _dirtyPlanIds is absent or empty, no plans are updated.
 */
const processCoverageBenefitPlans = (
  axiosInstance: AxiosInstance,
  coverage: RfpCoverage,
  benefitTransformOptions?: BenefitTransformToApiOptions,
): {
  promises: PlanSavePromise[];
  skippedFields: string[];
} => {
  if (!coverage.benefitPlans || !Array.isArray(coverage.benefitPlans)) {
    logger.warn("[BTX API] Skipping coverage without benefit plans:", {
      coverage,
    });
    return { promises: [], skippedFields: [] };
  }

  const dirtyIds = new Set(coverage._dirtyPlanIds ?? []);

  if (dirtyIds.size === 0) {
    logger.debug(
      "[BTX API] No dirty plans to update, skipping all benefit plans",
    );
    return { promises: [], skippedFields: [] };
  }

  type SaveResult =
    | { type: "saved"; promise: PlanSavePromise }
    | { type: "skipped"; fields: string[] };

  const dirtyPlans = coverage.benefitPlans.filter(
    (plan): plan is RfpBenefitPlan & { id: string } =>
      !!plan.id && dirtyIds.has(plan.id),
  );

  const results: SaveResult[] = dirtyPlans.map((benefitPlan) => {
    const payload = createBenefitPlanPayload(
      benefitPlan,
      coverage.type as LocCoverageType,
      benefitTransformOptions,
    );

    // Skip plans with incomplete benefits to prevent backend validation errors.
    // The plan stays dirty and will be retried on the next autosave.
    const incompleteFields = getIncompleteBenefitFields(payload);
    if (incompleteFields.length > 0) {
      logger.info(
        `[BTX API] Skipping benefit plan ${benefitPlan.id} - incomplete fields: ${incompleteFields.join(", ")}`,
      );
      return { type: "skipped" as const, fields: incompleteFields };
    }

    logger.debug(`[BTX API] Processing benefit plan:`, {
      planId: benefitPlan.id,
      benefitPlan,
    });

    // Note: Commission data is saved via useUpdateCommission hook in onCellValueChanged,
    // not through the saveRfp flow. This prevents duplicate API calls.
    const planId = benefitPlan.id;
    return {
      type: "saved" as const,
      promise: updateBenefitPlan(axiosInstance, planId, payload).then(
        (invalidFields) => ({ planId, invalidFields }),
      ),
    };
  });

  const savedResults = results.filter(
    (r): r is Extract<SaveResult, { type: "saved" }> => r.type === "saved",
  );
  const skippedResults = results.filter(
    (r): r is Extract<SaveResult, { type: "skipped" }> => r.type === "skipped",
  );

  return {
    promises: savedResults.map((r) => r.promise),
    skippedFields: [...new Set(skippedResults.flatMap((r) => r.fields))],
  };
};

/**
 * Updates a single coverage with batch changes
 *
 * Note: The BTX backend doesn't have a batch update endpoint yet.
 * This function will need to be implemented when the backend supports it,
 * or implemented as multiple individual benefit plan updates.
 *
 * @param request - Batch update request
 * @param axiosInstance - Optional axios instance for real API calls
 * @returns Promise resolving to update response
 *
 * @example
 * ```ts
 * const response = await updateCoverage({
 *   coverageId: "cov-123",
 *   coverageType: "dental",
 *   updates: [
 *     { path: "plan1.IN_NETWORK", value: "$1500", benefitKey: "annualMaximum" },
 *     { path: "plan1.OUT_OF_NETWORK", value: "$1000", benefitKey: "annualMaximum" }
 *   ]
 * }, axiosInstance);
 * ```
 */
export const updateCoverage = async (
  request: BatchUpdateCoverageRequest,
  axiosInstance?: AxiosInstance,
): Promise<BatchUpdateCoverageResponse> => {
  // If axios instance is provided, use real API
  if (axiosInstance) {
    // TODO: Implement batch update logic
    // The BTX backend currently supports PATCH /api/btx/v1/benefit-plans/:id
    // for individual plan updates. This function will need to:
    // 1. Group updates by benefit plan
    // 2. Make individual PATCH requests for each plan
    // 3. Or wait for a batch update endpoint to be added to the backend

    throw new Error("Batch update not yet implemented for real API");
  }

  // Fall back to mock implementation
  logger.info("[Mock API] Batch updating coverage:", { request });

  return {
    success: true,
    message: `Successfully updated ${request.updates.length} cells in ${request.coverageType} coverage`,
  };
};

/**
 * Batch update multiple cells in a coverage
 * Alias for updateCoverage for semantic clarity
 */
export const batchUpdateCoverage = updateCoverage;

/**
 * Saves complete RFP data
 *
 * For BTX backend, this function updates benefit plans using PATCH requests.
 * It only updates plans that have changed from the original data.
 *
 * @param request - Save RFP request
 * @param axiosInstance - Optional axios instance for real API calls
 * @returns Promise resolving to save response
 *
 * @example
 * ```ts
 * const response = await saveRfp({
 *   rfpId: "rfp-123",
 *   name: "Updated RFP",
 *   coverages: [...]
 * }, axiosInstance);
 * ```
 */
export const saveRfp = async (
  request: SaveRfpRequest,
  axiosInstance?: AxiosInstance,
): Promise<SaveRfpResponse> => {
  // If axios instance is provided, use real API
  if (axiosInstance) {
    logger.info("[BTX API] Saving RFP:", { rfpId: request.rfpId });

    // Validate that coverages is an array
    if (!request.coverages || !Array.isArray(request.coverages)) {
      const error = new Error("Invalid coverages data: must be an array");
      logger.error("[BTX API] Invalid coverages data", error, {
        requestCoverages: request.coverages,
      });
      throw error;
    }

    logger.info(`[BTX API] Processing ${request.coverages.length} coverages`);

    // Process all coverages and collect update promises
    const coverageResults = request.coverages.map((coverage) => {
      logger.debug(`[BTX API] Processing coverage type: ${coverage.type}`, {
        coverage,
      });
      return processCoverageBenefitPlans(
        axiosInstance,
        coverage,
        request.benefitTransformOptions,
      );
    });

    // Use allSettled so a single plan failure does not block other plans from saving.
    const allPromises = coverageResults.flatMap((r) => r.promises);
    const settled = await Promise.allSettled(allPromises);

    const fulfilledResults = settled
      .filter(
        (
          r,
        ): r is PromiseFulfilledResult<{
          planId: string;
          invalidFields: string[];
        }> => r.status === "fulfilled",
      )
      .map((r) => r.value);
    const savedPlanIds = fulfilledResults.map((r) => r.planId);
    const backendInvalidFields = fulfilledResults.flatMap(
      (r) => r.invalidFields,
    );

    // If plans were attempted but every PATCH failed (e.g. network error),
    // throw so React Query's onError fires instead of treating this as a success.
    if (allPromises.length > 0 && savedPlanIds.length === 0) {
      throw new Error("All benefit plan saves failed");
    }

    const frontendSkippedFields = [
      ...new Set(coverageResults.flatMap((r) => r.skippedFields)),
    ];

    return {
      success: true,
      message: `Successfully updated ${savedPlanIds.length} benefit plan(s)`,
      rfpId: request.rfpId,
      lastModified: new Date().toISOString(),
      savedPlanIds,
      skippedFields: [
        ...new Set([...frontendSkippedFields, ...backendInvalidFields]),
      ],
    };
  }

  // Fall back to mock implementation
  logger.info("[Mock API] Saving RFP:", { request });

  return {
    success: true,
    message: "RFP saved successfully",
    rfpId: request.rfpId,
    lastModified: new Date().toISOString(),
  };
};

/**
 * Upserts the content for an RFP note
 *
 * Uses BTX endpoint: PUT /btx/v1/rfps/{rfpId}/note
 *
 * @param rfpId - The RFP ID to upsert note for
 * @param request - The note upsert request (content)
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to updated RFP note data
 *
 * @example
 * ```ts
 * const updatedNote = await upsertRfpNote("rfp-123", { content: "New note" }, axiosInstance);
 * console.log(updatedNote.updatedAt);
 * ```
 */
export const upsertRfpNote = async (
  rfpId: string,
  request: RfpNotePayload,
  axiosInstance: AxiosInstance,
): Promise<RfpNotePayload> => {
  logger.debug("[BTX API] Upserting RFP note:", { rfpId });

  const response = await axiosInstance.put<RfpNotePayload>(
    `/btx/v1/rfps/${rfpId}/note`,
    request,
  );

  logger.debug("[BTX API] Upserted RFP note:", { rfpId });

  return response.data;
};

/**
 * Upserts the content for an analyst note
 *
 * Uses BTX endpoint: PUT /btx/v1/rfps/{rfpId}/analyst-note
 *
 * @param rfpId - The RFP ID to upsert an analyst note for
 * @param request - The note upsert request (content)
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to updated analyst note data
 *
 * @example
 * ```ts
 * const updatedNote = await upsertAnalystNote("rfp-123", { content: "New note" }, axiosInstance);
 * console.log(updatedNote.content);
 * ```
 */
export const upsertAnalystNote = async (
  rfpId: string,
  request: AnalystNotePayload,
  axiosInstance: AxiosInstance,
): Promise<AnalystNotePayload> => {
  logger.debug("[BTX API] Upserting analyst note:", { rfpId });

  const response = await axiosInstance.put<AnalystNotePayload>(
    `/btx/v1/rfps/${rfpId}/analyst-note`,
    request,
  );

  logger.debug("[BTX API] Upserted analyst note:", { rfpId });

  return response.data;
};

/**
 * Upsert (create or update) a coverage-level note
 *
 * @param rfpId - The RFP ID
 * @param coverageId - The coverage ID to associate with the note
 * @param content - The note content (max 1024 characters)
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to the RFP note response with metadata
 *
 * @example
 * ```ts
 * const response = await upsertCoverageNote("rfp-123", "cov-456", "Coverage note content", axiosInstance);
 * console.log(response.content);
 * ```
 */
export const upsertCoverageNote = async (
  rfpId: string,
  coverageId: string,
  content: string,
  axiosInstance: AxiosInstance,
  benefitPlanType?: string,
): Promise<RfpNotePayload> => {
  logger.debug("[BTX API] Upserting coverage note:", { rfpId, coverageId });

  const params = benefitPlanType
    ? `?benefitPlanType=${encodeURIComponent(benefitPlanType)}`
    : "";
  const response = await axiosInstance.put<RfpNotePayload>(
    `/btx/v1/rfps/${rfpId}/coverages/${coverageId}/note${params}`,
    { content },
  );

  logger.debug("[BTX API] Upserted coverage note:", { rfpId, coverageId });

  return response.data;
};
